import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDecimal } from "./formatting";
import { packagingFeeOf } from "./packagingFee";
import { orderTotalOf } from "./quoteTotals";
import { daRestituire } from "./pagamenti";
import { companyProfile } from "./companyProfile";
import { drawDocumentHeader } from "./documentHeader";
import {
  billedQuantity,
  boxUnitOf,
  requestedQuantityNote,
} from "./quoteItemUnits";
import { trackEvent, EVENTS } from "./analytics";
import { etichettaIva, riferimentoPredefinito } from "./nature";

function buildQuoteTable(quote, pageWidth, marginX, mqOverrides = {}) {
  const head = [
    [
      "Cod.",
      "Descrizione",
      "Q.tà",
      "U.m.",
      "Imponibile",
      "Aliquota",
      "Imposta",
      "Totale",
    ],
  ];

  const rows = [];
  let hasLineDiscount = false; // true se almeno una riga usa il prezzo scontato
  const itemMap = new Map((quote.items || []).map((it) => [it.id, it]));

  (quote.items || []).forEach((it) => {
    const name = it.itemName || it.subBrand || it.brand || "-";

    const { perBox, unit, hasPerBox } = boxUnitOf(it);
    const isBoxBased = perBox > 0;

    const rawQty =
      it?.QuoteItem?.quantity ??
      (isBoxBased ? it?.boxQuantity : it?.pieces) ??
      0;
    const baseQty = Number(rawQty);
    // Si fattura la scatola intera: la quantità in colonna è quella che il
    // cliente si porta via, non quella che aveva chiesto.
    const qty = isBoxBased
      ? billedQuantity({
          boxes: baseQty,
          perBox,
          hasPerBox,
          itemId: it.id,
          mqOverrides,
          persistedMq: it?.QuoteItem?.mqQuantity ?? it?.mqQuantity,
        })
      : baseQty;

    // Prezzo netto applicato (nuova semantica IVA esclusa): le righe storiche
    // migrate espongono il netto in discountedPrice; altrimenti è `price`.
    const dpThrough = it?.QuoteItem?.discountedPrice ?? it?.discountedPrice;
    const priceThrough = Number(it?.QuoteItem?.price ?? it.price ?? 0);
    const appliedUnitNet =
      dpThrough !== null && dpThrough !== undefined
        ? Number(dpThrough)
        : priceThrough;
    if (dpThrough !== null && dpThrough !== undefined) hasLineDiscount = true;
    // IVA della RIGA preventivo (through), poi fallback su articolo, poi 22.
    // Sempre ?? perché 0 (IVA 0%) è un valore valido.
    const iva = it?.QuoteItem?.iva ?? it?.iva ?? 22;
    const ivaRate = Number(iva ?? 0);
    // [sc-49176] Natura e riferimento normativo ci sono solo a IVA 0.
    const natura =
      ivaRate > 0 ? null : it?.QuoteItem?.natura ?? it?.natura ?? null;
    const legalReference = natura
      ? it?.QuoteItem?.legalReference ?? it?.legalReference ?? null
      : null;
    // Importi di RIGA (quantità inclusa): imponibile, imposta, totale lordo.
    const taxableAmount = Number((appliedUnitNet * qty).toFixed(2));
    const taxAmount = Number(((taxableAmount * ivaRate) / 100).toFixed(2));
    const grossTotal = Number((taxableAmount + taxAmount).toFixed(2));

    const dim =
      it.formatLabel ||
      (it.widthCm && it.heightCm ? `${it.widthCm}×${it.heightCm}` : null);
    const normStr = (s) => (s || "").replace(/×/g, "x").toLowerCase();
    const thickStr = it.thicknessMm ? `${parseFloat(it.thicknessMm)}mm` : null;
    // Formato, finitura e spessore compaiono quando l'articolo li ha, senza
    // chiedersi a quale categoria appartenga: un articolo che non li usa
    // semplicemente non ne ha, e la descrizione resta il solo nome.
    const specParts = [
      dim && !normStr(name).includes(normStr(dim)) ? dim : null,
      it.surface,
      thickStr && !name.toLowerCase().includes(thickStr) ? thickStr : null,
    ].filter(Boolean);
    const descBase = specParts.length
      ? `${name} ${specParts.join(" ")}`.trim()
      : name;
    // La quantità chiesta dal cliente resta sul documento anche quando si
    // fattura la scatola intera: senza, l'importo non si spiega da solo.
    const note = isBoxBased
      ? requestedQuantityNote({
          requested: it?.QuoteItem?.mqQuantity ?? it?.mqQuantity,
          billed: qty,
          boxes: baseQty,
          unit,
        })
      : null;
    const desc = note ? `${descBase}\n${note}` : descBase;

    rows.push({
      group: String(it.category || "Generale")
        .trim()
        .toUpperCase(),
      code: it.supplierCode || it.itemCode || it.code || "-",
      desc,
      qty,
      unit,
      taxable: taxableAmount,
      iva,
      natura,
      legalReference,
      tax: taxAmount,
      grossTotal,
    });
  });

  (quote.quote_items || []).forEach((qi) => {
    const id = qi?.item?.id;
    if (id && itemMap.has(id)) return;

    const it2 = qi?.item || {};
    const name = it2.itemName || "-";

    // qi.quantity è in scatole: va convertita in mq/ml come nel blocco sopra,
    // altrimenti l'importo di riga (prezzo al mq × scatole) risulta errato.
    const { perBox, unit, hasPerBox } = boxUnitOf(it2);
    const qty =
      perBox > 0
        ? billedQuantity({
            boxes: qi.quantity,
            perBox,
            hasPerBox,
            itemId: it2.id,
            mqOverrides,
            persistedMq: qi.mqQuantity,
          })
        : Number(qi.quantity || 0);

    // Prezzo netto applicato (nuova semantica IVA esclusa).
    const dpQi = qi.discountedPrice;
    const appliedUnitNet =
      dpQi !== null && dpQi !== undefined
        ? Number(dpQi)
        : Number(qi.price || 0);
    if (dpQi !== null && dpQi !== undefined) hasLineDiscount = true;
    const iva = qi?.iva ?? 22;
    const ivaRate = Number(iva ?? 0);
    const natura = ivaRate > 0 ? null : qi?.natura ?? null;
    const legalReference = natura ? qi?.legalReference ?? null : null;
    const taxableAmount = Number((appliedUnitNet * qty).toFixed(2));
    const taxAmount = Number(((taxableAmount * ivaRate) / 100).toFixed(2));
    const grossTotal = Number((taxableAmount + taxAmount).toFixed(2));

    const group = String(it2.category || "Generale")
      .trim()
      .toUpperCase();

    const dim2 =
      it2.formatLabel ||
      (it2.widthCm && it2.heightCm ? `${it2.widthCm}×${it2.heightCm}` : null);
    const normStr2 = (s) => (s || "").replace(/×/g, "x").toLowerCase();
    const thickStr2 = it2.thicknessMm
      ? `${parseFloat(it2.thicknessMm)}mm`
      : null;
    const specParts2 = [
      dim2 && !normStr2(name).includes(normStr2(dim2)) ? dim2 : null,
      it2.surface,
      thickStr2 && !name.toLowerCase().includes(thickStr2) ? thickStr2 : null,
    ].filter(Boolean);
    const desc2Base = specParts2.length
      ? `${name} ${specParts2.join(" ")}`.trim()
      : name;
    const note2 =
      perBox > 0
        ? requestedQuantityNote({
            requested: qi.mqQuantity,
            billed: qty,
            boxes: qi.quantity,
            unit,
          })
        : null;
    const desc2 = note2 ? `${desc2Base}\n${note2}` : desc2Base;

    rows.push({
      group,
      code: it2.supplierCode || it2.itemCode || it2.code || "-",
      desc: desc2,
      qty,
      unit,
      taxable: taxableAmount,
      iva,
      natura,
      legalReference,
      tax: taxAmount,
      grossTotal,
    });
  });

  const order = [];
  const groups = {};
  rows.forEach((r) => {
    if (!groups[r.group]) {
      groups[r.group] = [];
      order.push(r.group);
    }
    groups[r.group].push(r);
  });

  const body = [];
  order.forEach((g) => {
    body.push([
      {
        content: g,
        colSpan: 8,
        styles: {
          fillColor: [235, 235, 235],
          fontStyle: "bold",
          halign: "left",
        },
      },
    ]);
    groups[g].forEach((r) => {
      body.push([
        r.code,
        r.desc,
        String(r.qty).replace(".", ","),
        r.unit,
        `€ ${formatDecimal(r.taxable)}`,
        etichettaIva(r),
        `€ ${formatDecimal(r.tax)}`,
        `€ ${formatDecimal(r.grossTotal)}`,
      ]);
    });
  });

  const usable = pageWidth - 2 * marginX;
  const w = {
    cod: 0.09,
    qty: 0.08,
    um: 0.07,
    taxable: 0.15,
    aliquota: 0.07,
    imposta: 0.12,
    totale: 0.15,
  };
  const cw = {
    0: Math.floor(usable * w.cod),
    2: Math.floor(usable * w.qty),
    3: Math.floor(usable * w.um),
    4: Math.floor(usable * w.taxable),
    5: Math.floor(usable * w.aliquota),
    6: Math.floor(usable * w.imposta),
    7: Math.floor(usable * w.totale),
  };

  const rowsTaxableTotal = Number(
    rows.reduce((s, r) => s + Number(r.taxable || 0), 0).toFixed(2)
  );
  const rowsTaxTotal = Number(
    rows.reduce((s, r) => s + Number(r.tax || 0), 0).toFixed(2)
  );
  const rowsItemsTotal = Number(
    rows.reduce((s, r) => s + Number(r.grossTotal || 0), 0).toFixed(2)
  );

  // [sc-49176] Una riga per Natura usata, col riferimento della prima riga
  // che la porta (quello scritto sulla riga) o, in mancanza, quello predefinito.
  const riferimentiPerNatura = new Map();
  rows.forEach((r) => {
    if (!r.natura || riferimentiPerNatura.has(r.natura)) return;
    const testo = r.legalReference || riferimentoPredefinito(r.natura);
    if (testo) riferimentiPerNatura.set(r.natura, testo);
  });
  const riferimenti = [...riferimentiPerNatura].map(
    ([natura, testo]) => `${natura}: ${testo}`
  );

  return {
    head,
    body,
    riferimenti,
    hasLineDiscount,
    rowsTaxableTotal,
    rowsTaxTotal,
    rowsItemsTotal,
    tableWidth: usable,
    margin: { left: marginX, right: marginX },
    columnStyles: {
      0: { cellWidth: cw[0], halign: "left" },
      1: { cellWidth: "auto", minCellWidth: 30, halign: "left" },
      2: { cellWidth: cw[2], halign: "right" },
      3: { cellWidth: cw[3], halign: "center" },
      4: { cellWidth: cw[4], halign: "right" },
      5: { cellWidth: cw[5], halign: "center" },
      6: { cellWidth: cw[6], halign: "right" },
      7: { cellWidth: cw[7], halign: "right" },
    },
  };
}

/**
 * @param {object} params
 * @param {object} params.quote         - dati preventivo/ordine completi
 * @param {object} params.toast         - istanza vue-toastification ($toast)
 * @param {Function} params.uploadQuoteDocument - fn(payload) che esegue il dispatch Vuex
 * @param {Function} [params.onComplete]        - callback dopo upload riuscito
 */
export async function generateQuotePdf({
  quote,
  toast,
  uploadQuoteDocument,
  onComplete,
  isOrder = false,
  filenameOverride = null,
  mqOverrides = {},
}) {
  try {
    const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 10;
    const marginY = 10;
    let y = marginY;

    y = drawDocumentHeader(doc, { pageWidth, marginX, y });

    const printLabelValue = (
      label,
      value,
      xPos,
      yPos,
      maxWidth = null,
      link = null
    ) => {
      const labelCap = label.charAt(0).toUpperCase() + label.slice(1);
      doc.setFont("helvetica", "bold");
      doc.text(`${labelCap}:`, xPos, yPos);
      const off = doc.getTextWidth(`${labelCap}: `) + 0.8;
      doc.setFont("helvetica", "normal");
      const val = value || "N/D";
      let display = val;
      if (maxWidth) {
        while (
          doc.getTextWidth(display) > maxWidth - off &&
          display.length > 3
        ) {
          display = display.slice(0, -4) + "…";
        }
      }
      if (link) {
        doc.setTextColor(0, 0, 255);
        doc.textWithLink(display, xPos + off, yPos, { url: link });
        doc.setTextColor(0);
      } else {
        doc.text(display, xPos + off, yPos);
      }
      return yPos + 4;
    };

    const contactValue = quote?.Client?.pec || quote?.Client?.email || "";
    const contactLabel = quote?.Client?.pec ? "pec" : "email";
    const lh = (() => {
      const raw = quote?.Client?.legalHeadquarters;
      if (!raw) return {};
      if (typeof raw === "object") return raw;
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    })();
    const rightX = pageWidth / 2 - 10;
    const colW = pageWidth - rightX - marginX;
    const leftColW = rightX - marginX - 10;
    const formattedDate = new Date(quote.date).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    // Ultimo pagamento usato per generare il PDF. Calcolato qui (prima del
    // blocco "Dettaglio ordine") perché serve sia per la data documento sia,
    // più in basso, per la riga di riepilogo e i dettagli pagamento.
    const lastPayment = [...(quote.payments || [])].sort((a, b) => {
      const da = new Date(a?.paidAt ?? a?.createdAt ?? 0).getTime();
      const dbb = new Date(b?.paidAt ?? b?.createdAt ?? 0).getTime();
      if (da !== dbb) return da - dbb;
      return (Number(a?.id) || 0) - (Number(b?.id) || 0);
    })[(quote.payments || []).length - 1];

    const formattedDocDate = lastPayment
      ? new Date(
          lastPayment.paidAt ?? lastPayment.createdAt ?? Date.now()
        ).toLocaleDateString("it-IT", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : null;

    // LEFT: Dettaglio cliente
    let leftX = marginX;
    doc
      .setFontSize(12)
      .setFont("helvetica", "bold")
      .text("Dettaglio cliente", leftX, y);
    let yLeft = y + 8;
    doc.setFontSize(9);
    yLeft = printLabelValue("cliente", quote?.Client?.clientName, leftX, yLeft);
    yLeft = printLabelValue(
      "telefono",
      quote?.Client?.phoneNumber,
      leftX,
      yLeft
    );
    yLeft = printLabelValue(contactLabel, contactValue, leftX, yLeft);
    yLeft = printLabelValue(
      "partita iva",
      quote?.Client?.vatNumber,
      leftX,
      yLeft
    );
    yLeft = printLabelValue(
      "codice fiscale",
      quote?.Client?.taxCode,
      leftX,
      yLeft
    );
    yLeft = printLabelValue(
      "azienda",
      quote?.Client?.companyName,
      leftX,
      yLeft
    );
    yLeft = printLabelValue("sede legale", lh.address, leftX, yLeft, leftColW);

    // RIGHT: Dettaglio preventivo
    doc
      .setFontSize(12)
      .setFont("helvetica", "bold")
      .text(isOrder ? "Dettaglio ordine" : "Dettaglio preventivo", rightX, y);
    let yRight = y + 8;
    doc.setFontSize(9);
    if (lastPayment) {
      yRight = printLabelValue("data ordine", formattedDate, rightX, yRight);
      yRight = printLabelValue(
        "data documento",
        formattedDocDate,
        rightX,
        yRight
      );
    } else {
      yRight = printLabelValue("data", formattedDate, rightX, yRight);
    }
    // Modalità di evasione: in "ritiro" non c'è indirizzo di consegna.
    const isPickup = quote?.deliveryType === "ritiro";
    if (isPickup) {
      yRight = printLabelValue("modalità", "Ritiro", rightX, yRight, colW);
    } else {
      yRight = printLabelValue(
        "indirizzo di consegna",
        quote?.location?.address,
        rightX,
        yRight,
        colW,
        quote?.location?.link || null
      );
    }

    y = Math.max(yLeft, yRight) + 4;
    doc.setDrawColor(150).line(marginX, y, pageWidth - marginX, y);
    y += 6;

    const tbl = buildQuoteTable(quote, pageWidth, marginX, mqOverrides);
    autoTable(doc, {
      startY: y,
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 1.5 },
      headStyles: { fillColor: [200, 200, 200], fontStyle: "bold" },
      head: tbl.head,
      body: tbl.body,
      columnStyles: tbl.columnStyles,
      tableWidth: tbl.tableWidth,
      margin: tbl.margin,
    });
    y = (doc.lastAutoTable?.finalY || y) + 10;

    // [sc-48538 / task 48539] L'imballo si addebita solo se la casella era
    // spuntata, e l'importo è quello storicizzato sul preventivo — non 30 euro
    // cablati qui. Prima finiva su ogni documento, e siccome entra in `base`
    // falsava anche lo sconto percentuale: il totale a video e quello del PDF
    // non coincidevano, e quello sbagliato era il documento che va al cliente.
    const packagingFee = packagingFeeOf(quote);
    // [sc-48538 / task 48561] Il totale delle righe, maggiorazione compresa e
    // niente altro: sconto, acconto e imballo li applica il riepilogo qui
    // sotto. Prima, con una maggiorazione, si partiva da `realTotalPrice` —
    // che dal server arriva già al netto di sconto e acconto e già con
    // l'imballo dentro — e il documento li contava una seconda volta.
    const itemsTotal = orderTotalOf(quote, {
      rowsItemsTotal: tbl.rowsItemsTotal,
      hasLineDiscount: tbl.hasLineDiscount,
    });
    const base = itemsTotal + packagingFee;
    const percent = Number(quote.discount_percent || 0);
    const discountFromCol = Number(quote.discount || 0);
    const discountCalc = Number(((base * percent) / 100).toFixed(2));
    const discount = discountFromCol > 0 ? discountFromCol : discountCalc;
    const afterDiscount = Math.max(0, Number((base - discount).toFixed(2)));
    const deposit = Number(quote.deposit || 0);
    const toPay = Math.max(0, Number((afterDiscount - deposit).toFixed(2)));
    // [sc-48692] Pagato più del dovuto (il totale è sceso dopo il saldo): il
    // documento lo dice, invece di un «Totale da pagare: 0,00» che lo nasconde.
    const credito = daRestituire(afterDiscount - deposit);

    // Label + importo della riga pagamento basati sull'ultimo pagamento
    // registrato. "Totale pagato" mostra invece il cumulativo (deposit).
    const PAYMENT_TYPE_LABELS = {
      acconto: "Acconto",
      saldo_parziale: "Saldo parziale",
      saldo_finale: "Saldo finale",
    };
    const depositLabel = PAYMENT_TYPE_LABELS[lastPayment?.type] || "Acconto";

    const minVisualRoom = 55;
    if (pageHeight - y < minVisualRoom) {
      doc.addPage();
      y = marginY;
    }

    const headPadTop = 10;
    const lineH = 6;

    const leftTitle = "Riepilogo";
    // Formattazione display in euro in stile italiano (migliaia con punto,
    // decimali con virgola)
    const eur = (n) => formatDecimal(n);
    // [sc-48692] Il riepilogo è un conto: ogni riga ha l'importo allineato a
    // destra col suo segno, e una linea separa quello che compone il totale
    // dai pagamenti e dal risultato. Prima era una fila di «voce: importo»
    // tutte uguali, da cui non si capiva cosa si sommava a cosa.
    // Il meno è un trattino medio: il segno matematico non è nei font
    // standard di jsPDF e uscirebbe come un carattere a caso.
    const MENO = "\u2013";
    const dataBreve = (d) => {
      const t = new Date(d);
      return Number.isNaN(t.getTime())
        ? ""
        : t.toLocaleDateString("it-IT", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
          });
    };
    // Imponibile e IVA sono il dettaglio del totale articoli, non addendi:
    // per questo sono in grigio e senza segno.
    const leftRows = [
      { label: "Imponibile", amount: tbl.rowsTaxableTotal, tone: "muted" },
      { label: "IVA", amount: tbl.rowsTaxTotal, tone: "muted" },
      { label: "Totale articoli", amount: itemsTotal },
    ];
    // Una riga da zero euro non è un'informazione: se l'imballo non si
    // addebita, dell'imballo non si parla.
    if (packagingFee > 0) {
      leftRows.push({
        label: "Spese imballo",
        amount: packagingFee,
        sign: "+",
      });
    }
    if (discount > 0) {
      const pctLabel = percent ? ` (${percent}%)` : "";
      leftRows.push({
        label: `Sconto${pctLabel}`,
        amount: discount,
        sign: MENO,
      });
    }
    leftRows.push({ rule: true });
    leftRows.push({
      label: "Totale ordine",
      amount: afterDiscount,
      tone: "strong",
    });

    if (deposit > 0) {
      // Storico completo dei pagamenti, dal più vecchio, con la data: ogni
      // versamento resta tracciato.
      const pagamenti = [...(quote.payments || [])]
        .sort((a, b) => {
          const da = new Date(a.paidAt ?? a.createdAt ?? 0).getTime();
          const db = new Date(b.paidAt ?? b.createdAt ?? 0).getTime();
          return da - db;
        })
        .filter((p) => Number(p.amount || 0) > 0)
        .map((p) => ({
          label: `${PAYMENT_TYPE_LABELS[p.type] || "Acconto"} ${dataBreve(
            p.paidAt ?? p.createdAt
          )}`.trim(),
          amount: Number(p.amount),
        }));
      if (pagamenti.length > 1) {
        // Più versamenti: il dettaglio in grigio, e il totale che si sottrae.
        pagamenti.forEach((r) =>
          leftRows.push({ ...r, tone: "muted", indent: true })
        );
        leftRows.push({ label: "Totale pagato", amount: deposit, sign: MENO });
      } else if (pagamenti.length === 1) {
        // Uno solo: è lui la riga che si sottrae.
        leftRows.push({ ...pagamenti[0], sign: MENO });
      } else {
        // Nessun dettaglio, ma un versato cumulativo.
        leftRows.push({ label: depositLabel, amount: deposit, sign: MENO });
      }
      leftRows.push({ rule: true });
      leftRows.push(
        credito > 0
          ? {
              label: "Da restituire al cliente",
              amount: credito,
              tone: "strong",
            }
          : { label: "Totale da pagare", amount: toPay, tone: "strong" }
      );
    }

    const PAYMENT_METHOD_LABELS = {
      contanti: "Contanti",
      bonifico: "Bonifico",
      "carta di credito": "Carta di credito",
    };
    const centerTitle = "Dettagli pagamento";
    // Il profilo di un'azienda appena configurata può non avere ancora questi
    // dati: meglio un riquadro più corto che una riga «IBAN: undefined».
    const centerLines = [
      companyProfile.iban ? `IBAN: ${companyProfile.iban}` : null,
      companyProfile.name ? `Intestato a: ${companyProfile.name}` : null,
    ].filter(Boolean);
    // Metodo pagamento mostrato solo se presente sull'ultimo pagamento.
    if (lastPayment?.method) {
      const methodKey = String(lastPayment.method || "").toLowerCase();
      const methodLabel =
        PAYMENT_METHOD_LABELS[methodKey] || lastPayment.method;
      centerLines.unshift(`Metodo doc. corrente: ${methodLabel}`);
    }

    const rightTitle = "Firma";
    const sigLineYOffset = 12;

    // Colonna 1 misurata prima di disegnare: una voce lunga va a capo e
    // l'importo resta sulla sua prima riga, allineato a destra.
    const colW3 = (pageWidth - 2 * marginX) / 3;
    const colPad = 4; // padding interno colonna (mm)
    const col1X = marginX + colPad; // bordo sinistro colonna 1 (Riepilogo)
    const col1Right = marginX + colW3 - colPad;
    const ruleH = 3;
    const fontDi = (row) => {
      if (row.tone === "strong")
        doc.setFont("helvetica", "bold").setFontSize(10.5);
      else if (row.tone === "muted")
        doc.setFont("helvetica", "normal").setFontSize(9);
      else doc.setFont("helvetica", "normal").setFontSize(10);
    };
    const importoDi = (row) =>
      `${row.sign ? `${row.sign} ` : ""}€ ${eur(row.amount)}`;
    const layout = leftRows.map((row) => {
      if (row.rule) return { row, h: ruleH };
      fontDi(row);
      const x = col1X + (row.indent ? 3 : 0);
      const spazio = col1Right - x - doc.getTextWidth(importoDi(row)) - 3;
      const righe = doc.splitTextToSize(row.label, spazio);
      return { row, x, righe, h: righe.length * lineH };
    });
    const leftBody = layout.reduce((tot, l) => tot + l.h, 0);
    // L'ultima riga non ha bisogno dello spazio per quella dopo: sotto resta
    // solo il margine, come sopra il titolo.
    const ultimaRiga = layout.length ? layout[layout.length - 1].h : 0;

    const leftHeight = headPadTop + 7 + leftBody - ultimaRiga + 8;
    const centerHeight = headPadTop + 2 + 7 + centerLines.length * 8 + 8;
    const rightHeight = headPadTop + 2 + 7 + sigLineYOffset + 8;
    const minBlockH = 40;
    const blockH = Math.max(minBlockH, leftHeight, centerHeight, rightHeight);

    doc.setFillColor(240, 240, 240);
    doc.rect(marginX, y, pageWidth - 2 * marginX, blockH, "F");

    const col2X = marginX + colW3 + colPad; // bordo sinistro colonna 2 (Dettagli pagamento)
    const col3X = marginX + colW3 * 2.5; // Firma: invariata (centrata)

    // Colonna 1 — Riepilogo (allineata a sinistra)
    let fyLeft = y + headPadTop;
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text(leftTitle, col1X, fyLeft);
    fyLeft += 7;
    layout.forEach(({ row, x, righe, h }) => {
      if (row.rule) {
        // la linea sta a metà dello spazio che le è riservato
        doc.setDrawColor(170);
        doc.line(
          col1X,
          fyLeft - lineH / 2 + ruleH / 2,
          col1Right,
          fyLeft - lineH / 2 + ruleH / 2
        );
        fyLeft += h;
        return;
      }
      fontDi(row);
      doc.setTextColor(row.tone === "muted" ? 110 : 0);
      doc.text(righe, x, fyLeft);
      doc.text(importoDi(row), col1Right, fyLeft, { align: "right" });
      fyLeft += h;
    });
    doc.setTextColor(0);
    doc.setDrawColor(0);

    // Colonna 2 — Dettagli pagamento (allineata a sinistra)
    let fyCenter = y + headPadTop;
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text(centerTitle, col2X, fyCenter);
    fyCenter += 7;
    doc.setFont("helvetica", "normal").setFontSize(10);
    centerLines.forEach((ln, i) => {
      doc.text(ln, col2X, fyCenter + i * 8);
    });

    // Colonna 3 — Firma (centrata).
    // [sc-48692] Il titolo resta sulla riga degli altri due; la linea scende
    // in fondo al riquadro, all'altezza dell'ultima riga del riepilogo. Lo
    // spazio fra i due è quello in cui si firma.
    const sigWidth = colW3 * 0.6;
    const sigStart = col3X - sigWidth / 2;
    const sigEnd = col3X + sigWidth / 2;
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text(rightTitle, col3X, y + headPadTop, { align: "center" });
    const sigLineY = y + blockH - 8;
    doc.setDrawColor(0);
    doc.line(sigStart, sigLineY, sigEnd, sigLineY);

    y += blockH + 5;

    // ---- NOTE DEL PREVENTIVO ----
    if (quote.technical_queries) {
      const noteText = String(quote.technical_queries).trim();
      if (noteText) {
        doc.setFont("helvetica", "bold").setFontSize(9);
        doc.text("Note:", marginX, y);
        y += 5;
        doc.setFont("helvetica", "normal").setFontSize(8);
        const wrappedNote = doc.splitTextToSize(
          noteText,
          pageWidth - 2 * marginX
        );
        wrappedNote.forEach((ln) => {
          if (pageHeight - y < 10) {
            doc.addPage();
            y = marginY;
          }
          doc.text(ln, marginX, y);
          y += 4.5;
        });
        y += 4;
      }
    }

    // ---- RIFERIMENTI NORMATIVI (righe senza IVA) ----
    const riferimenti = tbl.riferimenti;
    if (riferimenti.length > 0) {
      const refLineH = 4.5;
      // Titolo e prima riga stanno insieme: il titolo non resta solo in fondo.
      if (pageHeight - y < 15) {
        doc.addPage();
        y = marginY;
      }
      doc.setFont("helvetica", "bold").setFontSize(9);
      doc.text("Riferimenti normativi:", marginX, y);
      y += 5;
      doc.setFont("helvetica", "normal").setFontSize(8);
      // Un riferimento può arrivare a 100 caratteri: va a capo, e la pagina si
      // cambia riga per riga, come nelle note.
      riferimenti.forEach((ref) => {
        doc.splitTextToSize(ref, pageWidth - 2 * marginX).forEach((ln) => {
          if (pageHeight - y < 10) {
            doc.addPage();
            y = marginY;
            doc.setFont("helvetica", "normal").setFontSize(8);
          }
          doc.text(ln, marginX, y);
          y += refLineH;
        });
      });
      y += 4;
    }

    // ---- CONDIZIONI DI PAGAMENTO ----
    const condizioni = [
      "1) Termini di pagamento: 40% anticipato all'ordine, saldo ad avviso merce pronta ns. deposito.",
      "2) Condizioni generali di vendita: L'autorizzazione al reso, o alla sostituzione, per quanto non conforme",
      "   all'ordine, deve essere preventivamente autorizzata dal produttore.",
      "3) Spese di deposito: Nel caso di mancato pagamento e/o ritardo nel ritiro e/o ingiustificato rifiuto della",
      "   merce da parte del cliente il venditore si riserva il diritto di addebitare al cliente i costi di deposito",
      "   della merce ex art. 1781 c.c. in misura di 30€ per ogni giorno di ritardo dalla data di presunta consegna.",
    ];
    const condLineH = 4.5;
    const condBlockH = 6 + condizioni.length * condLineH + 4;
    if (pageHeight - y < condBlockH) {
      doc.addPage();
      y = marginY;
    }
    doc.setTextColor(150).setFont("helvetica", "bold").setFontSize(9);
    doc.text("Condizioni di pagamento:", marginX, y);
    y += 5;
    doc.setFont("helvetica", "normal").setFontSize(8);
    condizioni.forEach((ln) => {
      doc.text(ln, marginX, y);
      y += condLineH;
    });
    doc.setTextColor(0);
    y += 4;

    const filenameBase = `${isOrder ? "Ordine" : "Preventivo"}_N_${
      quote.id
    }.pdf`;
    const isDev =
      process.env.NODE_ENV === "development" ||
      import.meta?.env?.MODE === "development" ||
      process.env?.VUE_APP_NODE_ENV === "development";

    const withSuffixBeforeExt = (filename, suffix) => {
      if (!suffix) return filename;
      const dot = filename.lastIndexOf(".");
      if (dot <= 0) return `${filename}.${suffix}`;
      return `${filename.slice(0, dot)}.${suffix}${filename.slice(dot)}`;
    };

    // Override esplicito (es. PDF pagamento "tipologia_YYYY-MM-DD_HH-mm.pdf"):
    // usato così com'è, senza suffisso ".dev". Senza override resta il
    // comportamento esistente del documento principale.
    const finalName = filenameOverride
      ? filenameOverride
      : withSuffixBeforeExt(filenameBase, isDev ? "dev" : "");
    const blob = doc.output("blob");
    const file = new File([blob], finalName, { type: "application/pdf" });

    try {
      await uploadQuoteDocument({
        quoteId: quote.id,
        document: file,
        filename: finalName,
      });
      toast.success("PDF generato e caricato con successo!");
      doc.save(finalName);
      // Solo tipo di documento ed esito: mai nome file, cliente o importi.
      trackEvent(EVENTS.QUOTE_EXPORTED, {
        action: "export",
        kind: isOrder ? "order" : "quote",
        feature: "pdf",
        outcome: "success",
      });
    } catch (error) {
      console.error("Errore durante l'upload del PDF:", error);
      trackEvent(EVENTS.QUOTE_EXPORTED, {
        action: "export",
        kind: isOrder ? "order" : "quote",
        feature: "pdf",
        outcome: "error",
      });
      toast.error(
        error.response?.data?.details ||
          error.response?.data?.error ||
          "Errore durante il caricamento del PDF."
      );
      return;
    }

    if (onComplete) await onComplete();
  } catch (error) {
    toast.error(
      error?.response?.data?.details ||
        error?.response?.data?.error ||
        "Errore durante la generazione/caricamento del PDF."
    );
  }
}
